import React, { useState } from 'react';
import { BiExpandAlt } from 'react-icons/bi';
import { css } from 'twin.macro';
import { styled } from 'twin.macro';

const PI = Math.PI;
const toRad = (deg) => ((deg - 90) * PI) / 180;

function describeArc(cx, cy, r, startDeg, endDeg) {
    const x1 = cx + r * Math.cos(toRad(startDeg));
    const y1 = cy + r * Math.sin(toRad(startDeg));
    const x2 = cx + r * Math.cos(toRad(endDeg));
    const y2 = cy + r * Math.sin(toRad(endDeg));
    const largeArc = endDeg - startDeg > 180 ? 1 : 0;
    return `M ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${largeArc} 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`;
}

const START_DEG = 270;
const END_DEG = 450;

function gaugeColor(pct) {
    if (pct < 50) return '#22c55e';
    if (pct < 75) return '#eab308';
    if (pct < 90) return '#f97316';
    return '#ef4444';
}

function formatUnitsShort(n) {
    // this is wront, we should get this from backend
    const hrs = Math.round((n / 4) * 10) / 10;
    return `${Number(n).toLocaleString()} units (${hrs} hrs)`;
}

const COMPACT = { W: 160, R: 58, SW: 12, CX: 80, CY: 72 };
const COMPACT_H = COMPACT.CY + COMPACT.SW / 2 + 6;

const EXPANDED = { W: 260, R: 96, SW: 16, CX: 130, CY: 110 };
const EXPANDED_H = EXPANDED.CY + EXPANDED.SW / 2 + 8;

function GaugeSVG({ pct, color, cfg, svgH }) {
    const { W, R, SW, CX, CY } = cfg;
    const fillEndDeg = START_DEG + (pct / 100) * 180;
    const bgPath = describeArc(CX, CY, R, START_DEG, END_DEG);
    const fillPath = pct > 0.1 ? describeArc(CX, CY, R, START_DEG, fillEndDeg) : null;
    const fontSize = cfg === COMPACT ? 22 : 36;

    return (
        <svg width={W} height={svgH} viewBox={`0 0 ${W} ${svgH}`} style={{ display: 'block', overflow: 'hidden' }}>
            <path d={bgPath} fill="none" stroke="#334155" strokeWidth={SW} strokeLinecap="round" />
            {fillPath && <path d={fillPath} fill="none" stroke={color} strokeWidth={SW} strokeLinecap="round" />}
            <text
                x={CX}
                y={CY - R * 0.28}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={color}
                fontSize={fontSize}
                fontWeight="700"
                fontFamily="inherit"
            >
                {pct.toFixed(1)}%
            </text>
            <text
                x={CX}
                y={CY - R * 0.28 + fontSize * 0.95}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="#64748b"
                fontSize={cfg === COMPACT ? 10 : 13}
                fontFamily="inherit"
            >
                used
            </text>
        </svg>
    );
}

const CardContainer = styled.div`
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    background: #1e293b;
    border-radius: 12px;
    padding: 10px 14px 12px;
    width: 168px;
    cursor: pointer;
    border: 1px solid #334155;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    user-select: none;
    position: relative;

    &:hover {
        border-color: #475569;
        box-shadow: 0 0 0 1px #47556933;
    }
`;

const ExpandedContainer = styled.div`
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    background: #1e293b;
    border-radius: 14px;
    padding: 14px 20px 18px;
    width: 280px;
    border: 1px solid ${(props) => props.color}44;
    box-shadow:
        0 0 0 1px ${(props) => props.color}22,
        0 4px 24px rgba(0, 0, 0, 0.4);
    user-select: none;
    position: relative;
`;

const ExpandIcon = styled(BiExpandAlt)`
    position: absolute;
    top: 8px;
    right: 10px;
    color: #64748b;
    font-size: ${(props) => (props.isExpanded ? '16px' : '12px')};
    cursor: pointer;
    z-index: 10;

    &:hover {
        color: #94a3b8;
    }
`;

const ContentWrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    animation: ${(props) => (props.isExpanding ? 'expandIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none')};
    animation-fill-mode: both;

    ${(props) =>
        props.isCollapsing &&
        css`
            animation: expandOut 0.25s cubic-bezier(0.4, 0, 0.2, 1);
            animation-fill-mode: both;
        `}

    @keyframes expandIn {
        0% {
            opacity: 0;
            transform: scale(0.92) translateY(-8px);
        }
        100% {
            opacity: 1;
            transform: scale(1) translateY(0);
        }
    }

    @keyframes expandOut {
        0% {
            opacity: 1;
            transform: scale(1) translateY(0);
        }
        100% {
            opacity: 0;
            transform: scale(0.92) translateY(-8px);
        }
    }
`;

const ProgressBar = styled.div`
    height: 4px;
    border-radius: 4px;
    background: #334155;
    overflow: hidden;

    > div {
        height: 100%;
        width: ${(props) => props.pct}%;
        background: ${(props) => props.color};
        border-radius: 4px;
        transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
    }
`;

const ChannelButton = styled.button`
    margin-top: 12px;
    background: none;
    border: 1px solid ${(props) => props.color}66;
    border-radius: 6px;
    color: ${(props) => props.color};
    font-size: 11px;
    padding: 4px 14px;
    cursor: pointer;
    width: 100%;
    transition: all 0.2s ease;

    &:hover {
        background: ${(props) => props.color}22;
        border-color: ${(props) => props.color}aa;
        transform: translateY(-1px);
    }
`;

const UnitGauge = ({ clientSummary = {} }) => {
    const [expanded, setExpanded] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [isCollapsing, setIsCollapsing] = useState(false);
    const clientData = clientSummary?.clientData || {};
    const channelId = clientData?.chatroom;
    const { totalAuthorized, totalRemaining, percentUsed } = clientSummary || {};
    const services = clientSummary?.services || [];

    const pct = Math.min(100, Math.max(0, percentUsed || 0));
    const color = gaugeColor(pct);

    const handleExpandClick = (e) => {
        e.stopPropagation();
        if (!expanded) {
            // Expanding
            setIsAnimating(true);
            setExpanded(true);
            setTimeout(() => setIsAnimating(false), 300);
        } else {
            // Collapsing
            setIsCollapsing(true);
            setTimeout(() => {
                setExpanded(false);
                setIsCollapsing(false);
            }, 250);
        }
    };

    const handleChannelClick = (e) => {
        e.stopPropagation();
        if (channelId) {
            window.open(`/teams/${channelId}`, '_blank');
        }
    };
    const clientName = `${clientData.firstName} ${clientData.lastName}`;

    // ── Compact card ──────────────────────────────────────────────────
    if (!expanded) {
        return (
            <CardContainer onClick={handleExpandClick} title="Click to expand">
                <ExpandIcon onClick={handleExpandClick} />
                <div
                    style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: '#e2e8f0',
                        marginBottom: 6,
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: 152,
                        marginTop: 4,
                    }}
                >
                    {clientName}
                </div>

                <GaugeSVG pct={pct} color={color} cfg={COMPACT} svgH={COMPACT_H} />

                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4, textAlign: 'center' }}>
                    {Number(totalRemaining).toLocaleString()} units remaining
                </div>

                <div style={{ fontSize: 9, color: '#475569', marginTop: 3 }}>tap to expand</div>
            </CardContainer>
        );
    }

    // ── Expanded card ─────────────────────────────────────────────────
    return (
        <ExpandedContainer color={color}>
            <ExpandIcon isExpanded={true} onClick={handleExpandClick} />
            <ContentWrapper isExpanding={isAnimating} isCollapsing={isCollapsing}>
                <div
                    style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: '#e2e8f0',
                        marginBottom: 8,
                        textAlign: 'center',
                        marginTop: 4,
                    }}
                >
                    {clientName}
                </div>

                <GaugeSVG pct={pct} color={color} cfg={EXPANDED} svgH={EXPANDED_H} />

                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6, textAlign: 'center' }}>
                    {formatUnitsShort(totalRemaining)} remaining
                </div>

                <div style={{ width: '100%', marginTop: 10 }}>
                    <ProgressBar pct={pct} color={color}>
                        <div />
                    </ProgressBar>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginTop: 3,
                            fontSize: 9,
                            color: '#475569',
                        }}
                    >
                        <span>0</span>
                        <span>{Number(totalAuthorized).toLocaleString()} authorized</span>
                    </div>
                </div>

                {services && services.length > 0 && (
                    <table
                        style={{
                            marginTop: 12,
                            fontSize: 11,
                            color: '#cbd5e1',
                            borderCollapse: 'collapse',
                            width: '100%',
                        }}
                    >
                        <thead>
                            <tr style={{ color: '#475569', borderBottom: '1px solid #334155' }}>
                                <th style={{ textAlign: 'left', paddingBottom: 4, paddingRight: 6, fontWeight: 500 }}>
                                    Code
                                </th>
                                <th style={{ textAlign: 'right', paddingBottom: 4, paddingRight: 6, fontWeight: 500 }}>
                                    Auth
                                </th>
                                <th style={{ textAlign: 'right', paddingBottom: 4, paddingRight: 6, fontWeight: 500 }}>
                                    Used
                                </th>
                                <th style={{ textAlign: 'right', paddingBottom: 4, paddingRight: 6, fontWeight: 500 }}>
                                    Left
                                </th>
                                <th style={{ textAlign: 'right', paddingBottom: 4, fontWeight: 500 }}>Week R.</th>
                            </tr>
                        </thead>
                        <tbody>
                            {services.map((svc, i) => (
                                <tr key={svc.serviceId || i} style={{ borderBottom: '1px solid #1e3a5f22' }}>
                                    <td
                                        style={{ paddingTop: 4, paddingRight: 6, color: '#e2e8f0', fontWeight: 500 }}
                                        title={`${svc.HCPCS} - ${svc.Description}`}
                                    >
                                        {svc.HCPCS}
                                    </td>
                                    <td style={{ textAlign: 'right', paddingTop: 4, paddingRight: 6 }}>
                                        {Number(svc.authorized).toLocaleString()}
                                    </td>
                                    <td style={{ textAlign: 'right', paddingTop: 4, paddingRight: 6 }}>
                                        {Number(svc.consumed).toLocaleString()}
                                    </td>
                                    <td
                                        style={{
                                            textAlign: 'right',
                                            paddingTop: 4,
                                            paddingRight: 6,
                                            color: gaugeColor((svc.consumed / svc.authorized) * 100 || 0),
                                        }}
                                    >
                                        {Number(svc.remaining).toLocaleString()}
                                    </td>
                                    <td style={{ textAlign: 'right', paddingTop: 4, color: '#94a3b8' }}>
                                        ~{Math.ceil(svc.recommendedWeeklyUnits)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                {services && services[0] && (
                    <div style={{ fontSize: 10, color: '#475569', marginTop: 8, alignSelf: 'flex-start' }}>
                        {services[0].weeksRemaining} weeks left in auth period
                    </div>
                )}

                {channelId && (
                    <ChannelButton color={color} onClick={handleChannelClick}>
                        Open Channel →
                    </ChannelButton>
                )}
            </ContentWrapper>
        </ExpandedContainer>
    );
};

export default UnitGauge;
